import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { settingsApi, type ImporterPermsSettings } from "@/api/settings";
import { Button } from "@/components/ui/Button";
import { Field, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

const sameSettings = (a: ImporterPermsSettings, b: ImporterPermsSettings) =>
  a.file_mode === b.file_mode &&
  a.dir_mode === b.dir_mode &&
  a.uid === b.uid &&
  a.gid === b.gid;

/**
 * Octal-mode validator. Accepts blank ("leave alone") + the same
 * forms the server's parse_mode helper takes — `"644"`, `"0644"`,
 * `"0o755"`. We do client-side validation to give the user immediate
 * feedback instead of a 422 round-trip; the server runs the same
 * check before persisting.
 */
const isValidMode = (raw: string): boolean => {
  const s = raw.trim();
  if (s === "") return true;
  const stripped = s.startsWith("0o") || s.startsWith("0O") ? s.slice(2) : s;
  return /^[0-7]+$/.test(stripped);
};

/** Decimal-id validator: blank or non-negative integer. */
const isValidId = (raw: string): boolean => {
  const s = raw.trim();
  if (s === "") return true;
  return /^\d+$/.test(s);
};

/**
 * Settings → Library Permissions (issue #25).
 *
 * Configures chmod/chown applied to every file the importer places
 * into the library root. Empty fields = "leave the file with whatever
 * the OS produced", which is the pre-#25 default and matches the
 * historical behaviour where the running umask + source mode bits
 * decide.
 *
 * The typical homelab configuration is `file_mode=644 dir_mode=755`
 * + a uid/gid that matches Plex's running user — that combination
 * lets Plex read everything we drop without needing a side-car
 * watcher / cron to fix permissions after the fact.
 */
export function LibraryPermissions() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const settingsQ = useQuery({
    queryKey: ["settings", "importer-perms"],
    queryFn: settingsApi.importerPerms.get,
  });

  const [draft, setDraft] = useState<ImporterPermsSettings | null>(null);

  useEffect(() => {
    if (!settingsQ.data) return;
    if (draft && !sameSettings(draft, settingsQ.data)) return;
    setDraft(settingsQ.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQ.data]);

  const dirty = useMemo(
    () => !!draft && !!settingsQ.data && !sameSettings(draft, settingsQ.data),
    [draft, settingsQ.data],
  );

  const fileModeBad = !!draft && !isValidMode(draft.file_mode);
  const dirModeBad = !!draft && !isValidMode(draft.dir_mode);
  const uidBad = !!draft && !isValidId(draft.uid);
  const gidBad = !!draft && !isValidId(draft.gid);
  const anyError = fileModeBad || dirModeBad || uidBad || gidBad;

  const saveMut = useMutation({
    mutationFn: (body: ImporterPermsSettings) =>
      settingsApi.importerPerms.set(body),
    onSuccess: (next) => {
      toast("Library permissions saved", "success");
      setDraft(next);
      qc.invalidateQueries({ queryKey: ["settings", "importer-perms"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!draft || anyError) return;
    saveMut.mutate({
      file_mode: draft.file_mode.trim(),
      dir_mode: draft.dir_mode.trim(),
      uid: draft.uid.trim(),
      gid: draft.gid.trim(),
    });
  };

  const update = (key: keyof ImporterPermsSettings, value: string) => {
    if (!draft) return;
    setDraft({ ...draft, [key]: value });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Settings"
        title="Library Permissions"
        description="Optional chmod and chown applied to every file and directory the importer creates in the library. Leave blank to keep the OS-default behaviour (umask + source-file mode)."
      />

      <form onSubmit={onSubmit} className="space-y-5">
        <section className="space-y-4 rounded-md border border-line bg-surface/40 px-5 py-4">
          <div>
            <h2 className="eyebrow">File mode and ownership</h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground/60">
              Applied after each hardlink / copy / rename into the library.
              Failures are non-fatal: the file is in place, only the mode or
              owner is wrong, and the operator can fix it out-of-band. On
              Windows these settings are ignored.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field
              label="File mode"
              hint="Octal, e.g. 644 or 664. Blank to leave files as the OS produced."
              error={fileModeBad ? "Must be octal (digits 0-7)" : undefined}
            >
              <TextInput
                value={draft?.file_mode ?? ""}
                placeholder="644"
                disabled={!draft || settingsQ.isLoading}
                onChange={(e) => update("file_mode", e.target.value)}
                spellCheck={false}
                className="font-mono"
              />
            </Field>

            <Field
              label="Directory mode"
              hint="Octal, e.g. 755 or 775. Applied to every per-series / per-album subdirectory we create."
              error={dirModeBad ? "Must be octal (digits 0-7)" : undefined}
            >
              <TextInput
                value={draft?.dir_mode ?? ""}
                placeholder="755"
                disabled={!draft || settingsQ.isLoading}
                onChange={(e) => update("dir_mode", e.target.value)}
                spellCheck={false}
                className="font-mono"
              />
            </Field>

            <Field
              label="Owner uid"
              hint="Numeric user id for chown. Typically Plex's uid in a shared homelab."
              error={uidBad ? "Must be a non-negative integer" : undefined}
            >
              <TextInput
                value={draft?.uid ?? ""}
                placeholder="1000"
                disabled={!draft || settingsQ.isLoading}
                onChange={(e) => update("uid", e.target.value)}
                spellCheck={false}
                className="font-mono"
                inputMode="numeric"
              />
            </Field>

            <Field
              label="Group gid"
              hint="Numeric group id for chown. Often a shared media group across arr-in-one and Plex."
              error={gidBad ? "Must be a non-negative integer" : undefined}
            >
              <TextInput
                value={draft?.gid ?? ""}
                placeholder="1000"
                disabled={!draft || settingsQ.isLoading}
                onChange={(e) => update("gid", e.target.value)}
                spellCheck={false}
                className="font-mono"
                inputMode="numeric"
              />
            </Field>
          </div>
        </section>

        <div className="flex items-center justify-end gap-3 border-t border-line pt-4">
          {dirty && !anyError && (
            <span className="text-xs text-foreground/55">Unsaved changes</span>
          )}
          <Button
            type="submit"
            loading={saveMut.isPending}
            disabled={!draft || !dirty || anyError}
          >
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
